import { Button, List, PasswordInput, Stack, ThemeIcon } from '@mantine/core';
import { IconCheck, IconCircleDashed } from '@tabler/icons-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { ApiError, officeApi } from '../api.js';
import { AuthScreen } from '../components/AuthScreen.js';
import { ErrorAlert } from '../components/ErrorAlert.js';
import { FormHeader } from '../components/FormHeader.js';
import { PATHS, passwordPageMode, pathForStage } from '../routing.js';
import { useSession } from '../session.js';

interface Values {
  current: string;
  next: string;
  confirm: string;
}

/** İstemcide aynı kural (ADR 0005 §5); karar her zaman sunucunundur. */
export function passwordRules(next: string, confirm: string, email: string | undefined) {
  const local = email?.split('@')[0]?.toLowerCase() ?? '';
  return {
    length: [...next].length >= 12,
    email: local.length < 3 || !next.toLowerCase().includes(local),
    match: next.length > 0 && next === confirm,
  };
}

/**
 * Parola sayfası: geçici parolada zorunlu ilk adım; açık oturumda kişinin kendi parolasını
 * değiştirmesi (ortak 2026-10-30; Settings ▸ Account ve kullanıcı menüsü). İkisinde de mevcut
 * parola istenir (uç `POST /api/office/auth/password`); diğer oturumlar kapanır.
 */
export function PasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const session = useSession();
  const [error, setError] = useState<string | null>(null);
  // Biçim açılışta belirlenir: ilk girişte parola kaydedilince aşama 'active' olur, sayfa profil
  // okunurken "Change password" biçimine geçmesin
  const [voluntary] = useState(() => passwordPageMode(session.stage ?? null) === 'voluntary');
  // Girişte yazılan geçici parola yalnız zorunlu adımda yeniden kullanılır
  const [known] = useState(() => (voluntary ? null : session.pendingPassword));
  const form = useForm<Values>({ defaultValues: { current: '', next: '', confirm: '' } });
  const next = form.watch('next');
  const confirm = form.watch('confirm');
  const rules = passwordRules(next, confirm, session.me?.user.email);
  const valid = rules.length && rules.email && rules.match;

  const submit = form.handleSubmit(async (values) => {
    setError(null);
    try {
      const { stage } = await officeApi.changePassword(known ?? values.current, values.next);
      if (voluntary) {
        navigate(PATHS.settings, { replace: true, state: { passwordChanged: true } });
        return;
      }
      session.setPendingPassword(null);
      if (stage === 'active') {
        // TOTP'si kapalı kişide parola değişince oturum doğrudan açılır (ADR 0148): menü için
        // profil. Okunamazsa da parola değişti ve oturum açık; aşama yerelde yazılır, pano açılır
        await session.refresh().catch(() => session.setStage(stage));
      } else {
        session.setStage(stage);
      }
      navigate(pathForStage(stage), { replace: true });
    } catch (failure) {
      const code = failure instanceof ApiError ? failure.code : 'GENERIC';
      // Sunucu girişteki kodu döner (ADR 0148 §8); bu sayfada e-posta alanı yok
      setError(code === 'INVALID_CREDENTIALS' ? 'INVALID_CURRENT_PASSWORD' : code);
    }
  });

  const rule = (ok: boolean, label: string) => (
    <List.Item
      icon={
        <ThemeIcon
          size={20}
          radius="xl"
          color={ok ? 'teal' : 'gray'}
          variant={ok ? 'filled' : 'light'}
        >
          {ok ? <IconCheck size={13} /> : <IconCircleDashed size={13} />}
        </ThemeIcon>
      }
      {...(ok ? {} : { c: 'dimmed' })}
    >
      {label}
    </List.Item>
  );

  return (
    <AuthScreen>
      {voluntary ? (
        <FormHeader title={t('password.changeTitle')} subtitle={t('password.changeSubtitle')} />
      ) : (
        // İki adımlı doğrulama artık isteğe bağlı (sahip 2026-11-01): "Step 1 of 2" yok
        <FormHeader
          eyebrow={t('password.eyebrow')}
          title={t('password.title')}
          subtitle={t('password.subtitle')}
        />
      )}
      <ErrorAlert code={error} />
      <form onSubmit={submit} noValidate>
        <Stack gap="md">
          {known ? null : (
            <PasswordInput
              label={voluntary ? t('password.currentPassword') : t('password.current')}
              autoComplete="current-password"
              required
              withAsterisk={false}
              {...form.register('current')}
            />
          )}
          <PasswordInput
            label={t('password.next')}
            autoComplete="new-password"
            autoFocus
            required
            withAsterisk={false}
            {...form.register('next')}
          />
          <PasswordInput
            label={t('password.confirm')}
            autoComplete="new-password"
            required
            withAsterisk={false}
            {...form.register('confirm')}
          />
          <List spacing={6} size="sm" aria-live="polite">
            {rule(rules.length, t('password.ruleLength'))}
            {rule(rules.email, t('password.ruleEmail'))}
            {rule(rules.match, t('password.ruleMatch'))}
          </List>
          <Button
            type="submit"
            size="md"
            fullWidth
            mt="xs"
            disabled={!valid}
            loading={form.formState.isSubmitting}
          >
            {t('password.submit')}
          </Button>
          {voluntary ? (
            <Button variant="subtle" fullWidth onClick={() => navigate(PATHS.settings)}>
              {t('password.cancel')}
            </Button>
          ) : null}
        </Stack>
      </form>
    </AuthScreen>
  );
}
